import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldCheck, KeyRound, FileCheck2, Eye, Lock, ArrowRight, Building2, GraduationCap } from "lucide-react";
import { PublicHeader } from "@/components/Layout";

const PILLARS = [
  {
    icon: KeyRound,
    title: "Cryptographic voucher codes",
    body: "Every voucher is generated with a CSPRNG and locks to the first credential it validates — so a code shared once cannot be re-purposed on another diploma.",
  },
  {
    icon: FileCheck2,
    title: "Institution-owned records",
    body: "Only registered partner institutions can issue certificates. Each record is auditable, tamper-evident, and traceable to a specific admin.",
  },
  {
    icon: Eye,
    title: "Zero-friction verification",
    body: "Employers, registrars, and other verifiers instantly authenticate credentials from any browser — no accounts, no downloads, no calls to the registrar's office.",
  },
  {
    icon: Lock,
    title: "Built for the long haul",
    body: "HttpOnly cookies, rate-limited endpoints, sliding-window abuse detection, magic-byte file validation, and weekly digest reports keep the network safe.",
  },
];

const STEPS = [
  { n: "01", title: "Institution issues a credential", body: "Partner schools onboard graduates via the CertiSeal dashboard — one-by-one or bulk-imported from CSV." },
  { n: "02", title: "Graduate receives credentials", body: "Each graduate gets their unique Certificate ID and a batch of voucher codes to share with verifiers." },
  { n: "03", title: "Verifier authenticates in seconds", body: "Anyone can enter the pair on CertiSeal.net to view the original credential — no login, no phone tag." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <PublicHeader />
      <main className="w-full max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 mb-6">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-semibold tracking-widest uppercase text-blue-700 dark:text-blue-300">About CertiSeal</span>
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight text-slate-900 dark:text-white leading-[1.05]" data-testid="about-title">
            Restoring trust in academic credentials.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            CertiSeal is a verification network for diplomas, certificates, and degrees. Institutions issue tamper-proof records; employers, registrars, and other verifiers authenticate them instantly — using nothing more than a Certificate ID and a Voucher Code.
          </p>
        </motion.div>

        {/* Mission */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">Our mission</div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-slate-900 dark:text-white">
              Every credential should be verifiable — in seconds, from anywhere.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-slate-600 dark:text-slate-400 leading-relaxed">
            <p>
              Diploma fraud costs employers billions every year and quietly undermines the graduates who earned their credentials the hard way. Traditional verification takes days of phone tag with a registrar — if the registrar even picks up.
            </p>
            <p>
              CertiSeal collapses that flow into a five-second check. Partner institutions retain full ownership of their records; we just make them addressable, tamper-evident, and instantly verifiable by anyone who holds the right pair of codes.
            </p>
          </div>
        </div>

        {/* Pillars */}
        <div className="mt-20 md:mt-28">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">What makes it work</div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-slate-900 dark:text-white">
              Four pillars of tamper-proof credentials.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map(({ icon: Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <Card className="p-7 h-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid={`pillar-${i}`}>
                  <div className="w-11 h-11 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white mb-2">{title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{body}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="mt-20 md:mt-28">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">How it works</div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-slate-900 dark:text-white">
              Three steps from issuance to verification.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map(({ n, title, body }, i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <Card className="p-7 h-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid={`step-${i}`}>
                  <div className="text-4xl font-display font-black text-blue-600/20 dark:text-blue-400/20 leading-none mb-3">{n}</div>
                  <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white mb-2">{title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{body}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 md:mt-28 rounded-2xl bg-slate-900 dark:bg-blue-950/20 border border-slate-800 dark:border-blue-950 p-10 md:p-14 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">
            Ready to verify?
          </h2>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">
            Have a Certificate ID and Voucher Code in hand? Authenticate a credential in under five seconds.
          </p>
          <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/verify">
              <Button size="lg" className="h-12 px-8 rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold" data-testid="about-verify-cta">
                <ShieldCheck className="w-4 h-4 mr-2 text-blue-600" /> Verify a credential
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/registry">
              <Button size="lg" variant="outline" className="h-12 px-8 rounded-full border-slate-700 text-white hover:bg-white/10" data-testid="about-registry-cta">
                <Building2 className="w-4 h-4 mr-2" /> Browse institutions
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
